import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { EntityInspectorResponse } from '../../services/api';
import { parseEntityId } from '../../lib/entityIds';
import { entityTypeColor, entityTypeIcon, entityTypeLabel } from '../../lib/entityMeta';
import { LoadingState, Metric, StatusBadge, Surface } from '../../design-system/v2';

interface EntityPreviewCardProps {
  entityId: string;
  label?: string;
  status?: string;
  data?: EntityInspectorResponse;
  loading?: boolean;
  error?: boolean;
}

const EntityPreviewCard: React.FC<EntityPreviewCardProps> = ({
  entityId,
  label,
  status,
  data,
  loading,
  error,
}) => {
  const parsed = parseEntityId(entityId);
  const type = data?.entity.type ?? parsed?.type ?? 'entity';
  const displayName = data?.entity.display_name ?? label ?? parsed?.id ?? entityId;
  const displayStatus = data?.entity.status ?? status;
  const color = entityTypeColor(type);

  if (loading) {
    return (
      <Surface level="flat" pad="sm" sx={{ minWidth: 220, maxWidth: 300, height: 'auto' }}>
        <LoadingState variant="skeleton" rows={2} label="" />
      </Surface>
    );
  }

  return (
    <Surface level="flat" pad="sm" sx={{ minWidth: 220, maxWidth: 300, height: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            bgcolor: alpha(color, 0.12),
          }}
        >
          {entityTypeIcon(type)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <StatusBadge label={entityTypeLabel(type)} tone="neutral" />
          <Box
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: '0.875rem',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            component="div"
          >
            {displayName}
          </Box>
        </Box>
      </Box>
      {displayStatus && (
        <StatusBadge label={displayStatus} tone="info" />
      )}
      <Box
        sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem', color: 'text.secondary', mt: 0.5, wordBreak: 'break-all' }}
        component="div"
      >
        {entityId}
      </Box>
      {error && !data && (
        <Box sx={{ fontSize: '0.75rem', color: 'warning.main', mt: 0.5 }} component="div">
          Preview unavailable — click to inspect
        </Box>
      )}
      {data?.relationships?.length ? (
        <Metric
          label="Relationships"
          value={data.relationships.length}
          compact
        />
      ) : null}
    </Surface>
  );
};

export default EntityPreviewCard;
